import * as amplitude from '@amplitude/unified';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';

export type ChatModelOption = {
  id: string;
  label: string;
};

export type UserSettings = {
  can_upload_files_to_agent: boolean;
  can_access_data_api?: boolean;
  // Chat models this user may pick between. Labels come from the server so
  // the frontend doesn't carry its own copy of the catalogue; a single entry
  // means the picker is hidden.
  supported_models?: ChatModelOption[];
  default_model?: string;
};

export type CurrentUser = {
  id?: number;
  email?: string;
  name?: string;
  timezone?: string | null;
  // WhatsApp number linked to the account (E.164 digits, no '+'), or null.
  phone_number?: string | null;
  phone_verified_at?: string | null;
  settings?: UserSettings;
};

export const currentUserKey = ['user', 'current'] as const;

// Best-effort browser tz detection. The backend validates the value via
// zoneinfo.ZoneInfo so we don't enumerate IANA names client-side.
const detectBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Per-user-id guard so concurrent or double-invoked fetches (React
// StrictMode in dev, invalidations) don't fire the PUT more than once. We
// can't rely on the server response alone — both calls see timezone:null
// until the first PUT lands.
const _autoTimezonePutAttempted = new Set<number>();

async function fetchCurrentUser (): Promise<CurrentUser> {
  const client = await OilyticsClient.create();
  const { data: { results } } = await client.getCurrentUser();
  const user = results as CurrentUser;

  if (user?.email) {
    Sentry.setUser({ email: user.email });
    if (import.meta.env.VITE_AMPLITUDE_KEY) {
      amplitude.setUserId(user.email);
      amplitude.track('authenticated');
    }
  }

  // Auto-capture browser timezone the first time we see a user without one
  // persisted. The server response is the source of truth — once persisted,
  // subsequent /api/user/current calls return the saved value and this
  // branch short-circuits.
  if (user?.id && !user.timezone && !_autoTimezonePutAttempted.has(user.id)) {
    _autoTimezonePutAttempted.add(user.id);
    const tz = detectBrowserTimezone();
    client.setUserTimezone(tz).catch((err) => {
      // Clear the guard on failure so a later refetch can retry.
      _autoTimezonePutAttempted.delete(user.id as number);
      // Non-fatal: the user can still chat without a stored tz; the
      // chatbot/email path will Sentry-alert and fall back to UTC.
      Sentry.captureException(err);
    });
  }

  return user;
}

// The logged-in user from /api/user/current. Fetched once per session
// (staleTime Infinity); TimezoneSettingsDialog invalidates currentUserKey
// after a save so consumers pick up the new value.
export function useCurrentUser (options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: currentUserKey,
    queryFn: fetchCurrentUser,
    staleTime: Infinity,
    ...options,
  });
}
